import React, { forwardRef } from 'react'
import { Money } from '@s4l/stayforlong/kernel/Money'
import styles from './index.module.css'
import { Typography } from '@s4l/ds'
import { cn } from '@s4l/utility-css'
import { IconHeartFull } from '@s4l/icons/IconHeartFull'
import type { useS4LContext } from '@s4l/react-context'
import type { Markets } from '@s4l/stayforlong/shared/domain/Market'
import { Market } from '@s4l/stayforlong/shared/domain/Market'

export interface MapMarkerProps {
  /**
   * The money in JSON
   */
  moneyJSON?: ReturnType<Money['toJSON']>
  /**
   * If the marker is active
   */
  active?: boolean
  /**
   * If the marker is disabled
   */
  disabled?: boolean
  /**
   * If the marker is visited
   */
  visited?: boolean
  /**
   * If the marker is liked
   */
  liked?: boolean

  /**
   * The onClick handler
   */
  onClick?: (event: React.MouseEvent) => void

  /**
   * The data-testid to pass to the button
   */
  'data-testid'?: string

  /**
   * The locale to use for the money formatting
   */
  i18n: ReturnType<typeof useS4LContext>['i18n']

  /**
   * The market
   */
  market: Markets
}

const MapMarker = forwardRef<HTMLButtonElement, MapMarkerProps>(function MapMarker(
  { moneyJSON, active, disabled, visited, liked, onClick, 'data-testid': dataTestId, market, i18n },
  ref
) {
  const money = Money.fromJSONOrUndefined(moneyJSON)

  if (!money) return null

  let text: string
  try {
    text = money.toFormattedString(i18n.locale, Market.create({ value: market }))
  } catch {
    return null
  }

  return (
    <button
      type="button"
      ref={ref}
      data-currency={money.currency}
      data-amount={money.amount}
      className={cn(styles.mapMarker, {
        [styles.active]: !!active,
        [styles.disabled]: !!disabled,
        [styles.visited]: !!visited,
        [styles.liked]: !!liked,
      })}
      onClick={onClick}
      data-testid={dataTestId}
    >
      {liked && <IconHeartFull size={16} color="var(--color-primary-50)" />}
      <Typography
        color={disabled ? 'color-mix(in srgb, var(--color-neutral-900) 25%, transparent)' : 'white'}
        variant="caption"
        size="l"
        bold
      >
        {text}
      </Typography>
    </button>
  )
})

MapMarker.displayName = 'MapMarker'
export default MapMarker
